/* eslint-disable import/first */
import React, { lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { FormattedMessage } from 'react-intl'
import { Switch, Route, Redirect } from 'react-router-dom'
import { WorkawareApi } from 'apis/WorkawareApi'
import Navigation from 'components/navigation'
import { userLoaded, userUnloaded, userModuleAccessLoaded, userModuleAccessCleared, setLocale, setDateFormat, setTimeFormat } from 'reducers/userReducer'
import { setPageTitle } from 'reducers/stateReducer'

/* font-awesome library */
import { library } from '@fortawesome/fontawesome-svg-core'
import { faFile, faFileAlt, faFilePdf, faFileInvoice, faFileCode, faBook, faFileSignature, faGlobeAmericas, faFileWord } from '@fortawesome/free-solid-svg-icons'
import { parseUrl } from 'utils/utilities'
import ErrorBoundary from 'components/ErrorBoundary'
import { IRootReduxState } from 'types/reducers'
import SecurityPage from './pages/settings/SecurityPage'
import RasterPage from './pages/settings/RasterPage'
import UserGeneratedShapesPage from './pages/settings/UserGeneratedShapesPage'
import Forbidden from './Forbidden'
import ProtectedRoute from 'components/ProtectedRoute'
import { Module } from 'enums/Module'
import { ModuleAccessProvider } from 'utils/moduleAccessProvider'
import { getIsManagerLike } from 'utils/permissionHelpers'
import ShapeFilesPage from './pages/settings/ShapeFilesPage'
import CompanyMessageDialog from '../../src/containers/dialogs/CompanyMessageDialog'
import { CompanyMessageModel } from 'data/models/CompanyMessageModel'
import GlobalSettingsPage from './pages/settings/GlobalSettingsPage'
import AIChatComponent from 'components/buttons/AIChatComponent'
import SessionTimeoutManager from 'components/SessionTimeoutManager'

library.add(faFile, faFileAlt, faFilePdf, faFileWord, faFileInvoice, faFileSignature, faFileCode, faBook, faGlobeAmericas)

/* on-demand loading */
const LiveReporting = lazy(() => import('containers/pages/LiveReporting'))
const ReportsPage = lazy(() => import('containers/pages/reports/ReportsPage'))
const Home = lazy(() => import('containers/pages/Home'))
const UserDashboard = lazy(() => import('containers/pages/UserDashboard'))
const Library = lazy(() => import('containers/pages/Library'))
const AssignmentsByYou = lazy(() => import('containers/pages/assignments/AssignmentsByYou'))
const AssignmentsToYou = lazy(() => import('containers/pages/assignments/AssignmentsToYou'))
const AssignmentsOverdue = lazy(() => import('containers/pages/assignments/AssignmentsOverdue'))
const Organization = lazy(() => import('containers/pages/Organization'))
const Callback = lazy(() => import('containers/pages/Callback'))
const Bye = lazy(() => import('containers/pages/Bye'))
const CompletedForms = lazy(() => import('containers/pages/CompletedForms'))
const SettingsPage = lazy(() => import('containers/pages/settings/SettingsPage'))
const UserProfilePage = lazy(() => import('containers/pages/settings/UserProfilePage'))
const UserListPage = lazy(() => import('containers/pages/settings/UserListPage'))
const EmailTemplatesPage = lazy(() => import('containers/pages/settings/EmailTemplatesPage'))
const PicklistSettingsPage = lazy(() => import('containers/pages/settings/PicklistSettingsPage'))
const FormsSettingsPage = lazy(() => import('containers/pages/FormTemplatesPage'))
const AnonymousFormsPage = lazy(() => import('containers/pages/settings/AnonymousFormsPage'))
const CultureGroups = lazy(() => import('containers/pages/CultureGroups'))
const DynamicFormViewer = lazy(() => import('containers/pages/DynamicFormViewer'))
const LockScreen = lazy(() => import(`containers/pages/LockScreen`))
const LoginPage = lazy(() => import(`containers/pages/LoginPage`))
const TwoFactorPage = lazy(() => import(`containers/pages/TwoFactorPage`))
const ResetPasswordPage = lazy(() => import('containers/pages/ResetPasswordPage'))
const AssetMaintenanceDashboard = lazy(() => import('containers/pages/AssetMaintenanceDashboard'))
const Calendar = lazy(() => import(`containers/pages/Calendar`))
const JourneyManagementDashboard = lazy(() => import('containers/pages/JourneyManagement'))
const LocationTrackingDashboard = lazy(() => import('containers/pages/LocationTracking'))
const SitesLocations = lazy(() => import(`containers/pages/assets/SitesLocations`))
const Picklists = lazy(() => import(`containers/pages/assets/Picklists`))
const MaintenanceSchedules = lazy(() => import(`containers/pages/assets/MaintenanceSchedules`))
const AssetsManagement = lazy(() => import(`containers/pages/assets/AssetsManagement`))
const AssetDetailPage = lazy(() => import(`containers/pages/assets/AssetDetailPage`))
const AssetEditRequestResolvePage = lazy(() => import(`containers/pages/assets/AssetEditRequestResolvePage`))
const CompanyMessagesPage = lazy(() => import('containers/pages/settings/CompanyMessagesPage'))
const EmailLogPage = lazy(() => import(`containers/pages/settings/EmailLogPage`))
const EventsPage = lazy(() => import(`containers/pages/settings/EventsPage`))
const CameraSettingPage = lazy(() => import(`containers/pages/settings/CameraSettingPage`))
const SMSLogPage = lazy(() => import(`containers/pages/settings/SMSLogPage`))
const Counters = lazy(() => import(`containers/pages/settings/Counters`))
const TrackingPage = lazy(() => import(`containers/pages/settings/TrackingPage`))
const DirectMessage = lazy(() => import(`containers/pages/settings/DirectMessagePage`))
const AssignmentStatusPage = lazy(() => import(`containers/pages/settings/AssignmentStatusPage`))
const UserCertificates = lazy(() => import(`containers/pages/trainingMatrix/UserCertificates`))
const EditUserCourses = lazy(() => import(`containers/pages/trainingMatrix/EditUserCourses`))
const UserPolicies = lazy(() => import(`containers/pages/trainingMatrix/UserPolicies`))
const EditUserPolicyCompliance = lazy(() => import(`containers/pages/trainingMatrix/EditUserPolicyCompliance`))
const Courses = lazy (() => import(`containers/pages/trainingMatrix/Courses`))
const PrivateCourses = lazy(() => import(`containers/pages/trainingMatrix/Privatecourses`))
const CompanyPolicies = lazy(() => import(`containers/pages/trainingMatrix/CompanyPolicies`))
const TrainingMatrixConfiguration = lazy(() => import(`containers/pages/trainingMatrix/TrainingMatrixConfiguration`))
const RecurringAssignment =lazy(() => import(`containers/pages/assignments/RecurringAssignment`))
const ScheduledItems = lazy(() => import(`containers/pages/ScheduledItems`))
const ScheduledEventTypePage = lazy(() => import(`containers/pages/settings/ScheduledEventTypePage`))
const ScheduledStatusTypePage = lazy(() => import(`containers/pages/settings/ScheduledStatusTypePage`))
const ScheduledVisibilityTypePage = lazy(() => import(`containers/pages/settings/ScheduledVisibilityTypePage`))
const PublicAnonymousFormPage = lazy(() => import('containers/pages/PublicAnonymousFormPage'))
const SsoCallback = lazy(() => import('containers/pages/SsoCallback'))
const SsoRelay = lazy(() => import('containers/pages/SsoRelay'))

import('../../package.json').then((packages) => {
  var params = new URLSearchParams(window.location.search)
  if (params.has('package-version')) {
    const p = params.get('package-version')
    console.log(`${p}: ${packages.dependencies[params.get('package-version') as string]}`)
  }
})

const NoMatch: React.FC = (props) => {
  const m = parseUrl(window.location.href)
  const { error } = m?.parameters as any
  return (
    <section className="hero">
      <div className="hero-body">
        <div className="container">
          <h1 className="title">Unknown Route =(</h1>
          <h2 className="subtitle">We could not find a match to the provided route</h2>
          <ErrorBoundary>{error && <pre>{JSON.stringify(JSON.parse(decodeURIComponent(error)), null, 2)}</pre>}</ErrorBoundary>
        </div>
      </div>
    </section>
  )
}

const getAnonymousRoutes = () => (
  <Switch>
    <Route exact path="/bye" render={() => <Bye />} />
    <Route exact path="/callback" render={() => <Callback />} />
    <Route exact path="/auth/callback" render={() => <SsoCallback />} />
    <Route exact path="/sso-relay" render={() => <SsoRelay />} />
    <Route exact path="/reset-password" render={() => <ResetPasswordPage />} />
    <Route exact path="/login/2fa" render={() => <TwoFactorPage />} />
    <Route render={() => <LoginPage />} />
  </Switch>
)

// Mounts inside <Suspense> — fires onReady once lazy routes have resolved and painted.
const AppReadyNotifier: React.FC<{ onReady: () => void }> = ({ onReady }) => {
  React.useEffect(() => {
    onReady()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}

interface Props { }
const App: React.FC<Props> = (props) => {
  const dispatch = useDispatch()
  const user = useSelector((state: IRootReduxState) => state.oidc.user)
  const profile = useSelector((state: IRootReduxState) => state.app.user.current)
  const access = useSelector((state: IRootReduxState) => state.app.user.access)
  const moduleAccess = new ModuleAccessProvider(access)
  const [companyMessage, SetCompanyMessage] = React.useState<CompanyMessageModel | undefined>(undefined)
  const [open, setOpen] = React.useState<boolean>(true)
  const [appReady, setAppReady] = React.useState<boolean>(false)
  const [dialogReady, setDialogReady] = React.useState<boolean>(false)
  // Company messages are for regular users and other roles only: never shown to admin / manager
  // roles. Wait until the profile and its Role are hydrated so admins don't see a brief flash.
  const roleResolved = !!profile && (profile.RoleId == null || !!profile.Role)
  const canSeeCompanyMessage = roleResolved && !getIsManagerLike(profile)

  // Wait for Suspense to resolve (appReady) AND the page's own data-fetch to render
  // before showing the company message dialog.
  React.useEffect(() => {
    if (!appReady || !companyMessage) return
    const timer = setTimeout(() => setDialogReady(true), 1500)
    return () => clearTimeout(timer)
  }, [appReady, companyMessage])

  const Acknowledge = () => {
    setOpen(false)
    const api = new WorkawareApi()
    api.companyMessage.acknowledge()
      .catch((error) => {
        console.error('Failed to acknowledge company message:', error)
      })
  }
  const getLoggedInRoutes = () => (
    <Switch>
      <Route exact path="/" render={() => {
        if (!profile) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )
        }

        // If the role has Dashboard access, render it.
        if (moduleAccess.getAccess(Module.Dashboard).AccessAllowed) {
          if (profile.Role?.IsAdminLike || profile.SecurityLevel === 3) {
            return <Home />
          }
          return <UserDashboard />
        }

        // Dashboard is blocked — redirect to the first module the user can access.
        const fallbackRoutes: Array<{ module: Module; path: string }> = [
          { module: Module.Reports,             path: '/reports' },
          { module: Module.Calendar,            path: '/calendar' },
          { module: Module.JourneyManagement,   path: '/journeyManagement' },
          { module: Module.ScheduledItems,      path: '/ScheduledItems' },
          { module: Module.DocumentLibrary,     path: '/library/documents' },
          { module: Module.TrainingMatrix,      path: '/trainingmatirx/usercertificates' },
          { module: Module.CompletedForms,      path: '/forms/completed' },
          { module: Module.BlankForms,          path: '/form/templates' },
          { module: Module.Assignments,         path: '/assignments/assignedToYou' },
          { module: Module.CMMSAssets,          path: '/assets/maintenance' },
          { module: Module.LocationTracking,    path: '/locationTracking' },
          { module: Module.CompanySettings,     path: '/settings/company' },
          { module: Module.OrganizationChart,   path: '/UsersList' },
        ]
        const firstAccessible = fallbackRoutes.find(
          (r) => moduleAccess.getAccess(r.module).AccessAllowed
        )
        if (firstAccessible) {
          return <Redirect to={firstAccessible.path} />
        }

        // No module access at all — show a friendly message.
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#334155' }}>No Access</div>
            <div style={{ fontSize: 14, color: '#64748b' }}>Your role does not have access to any module. Please contact your administrator.</div>
          </div>
        )
      }} />
      <Route exact path="/callback" render={() => <Callback />} />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.InteractiveCharts)}
        exact
        path="/reports/live/:id?"
        render={(props) => {
          return <LiveReporting {...props} />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Reports)}
        exact
        path="/reports"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.reports" defaultMessage="Reports" />,
              props.location.pathname
            )
          )
          return <ReportsPage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.CMMSAssets)}
        exact
        path="/assets/maintenance"
        render={(props) => {
          return <AssetMaintenanceDashboard {...props} />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Calendar)}
        exact
        path="/calendar"
        render={(props) => {
          dispatch(
            setPageTitle(<FormattedMessage id="title.calendar" defaultMessage="Calendar" />, props.location.pathname)
          )
          return <Calendar {...props} />
        }}
      />
      <Route
        exact
        path="/dynamic/:id?"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="dynamicForm.pageTitle" defaultMessage="Dynamic Form" />,
              props.location.pathname
            )
          )
          return <DynamicFormViewer {...props} />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Assignments)}
        exact
        path="/assignments/assignedToYou"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.assignments.to-you" defaultMessage="Assignments" />,
              props.location.pathname
            )
          )
          return <AssignmentsToYou />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Assignments)}
        exact
        path="/assignments/assignedByYou"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.assignments.by-you" defaultMessage="Assignments" />,
              props.location.pathname
            )
          )
          return <AssignmentsByYou />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Assignments)}
        exact
        path="/assignments/overdue"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.assignments.overdue" defaultMessage="Overdue Assignments" />,
              props.location.pathname
            )
          )
          return <AssignmentsOverdue />
        }}
      />
      <ProtectedRoute
      access ={moduleAccess.getAccess(Module.Assignments)}
      exact
      path="/assignment/recurring"
      render={(props) => {
        dispatch(
          setPageTitle(
            <FormattedMessage id="title.assignments.recurring" defaultMessage="Assignments" />,
            props.location.pathname
          )
        )
        return <RecurringAssignment />
      }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.DocumentLibrary)}
        exact
        path="/library/documents"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.Documents" defaultMessage="Documents" />,
              props.location.pathname
            )
          )
          return <Library />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.DocumentLibrary)}
        exact
        path="/library/cultureGroups"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.culture-groups" defaultMessage="Document Library / Culture Groups" />,
              props.location.pathname
            )
          )
          return <CultureGroups />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.OrganizationChart)}
        exact
        path="/organization"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.organization" defaultMessage="Team Organization" />,
              props.location.pathname
            )
          )
          return <Organization />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.CompletedForms)}
        exact
        path="/forms/completed"
        render={() => {
          return <CompletedForms />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.BlankForms)}
        exact
        path={['/form/templates/anonymous', '/form/templates/anonymous/:id', '/form/templates/anonymous/:action/:id']}
        render={() => {
          return <AnonymousFormsPage />
        }}
      />
        <ProtectedRoute
        access={moduleAccess.getAccess(Module.ScheduledItems)}
        exact
        path="/ScheduledItems"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.ScheduledItems" defaultMessage="Scheduled Items" />,
              props.location.pathname
            )
          )
          return <ScheduledItems />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.ScheduledItems)}
        exact
        path="/ScheduledItems/eventTypes"
        render={(props) => {
          dispatch(setPageTitle(<FormattedMessage id="menu.main.scheduledItems.eventTypes" defaultMessage="Event Type" />, props.location.pathname))
          return <ScheduledEventTypePage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.ScheduledItems)}
        exact
        path="/ScheduledItems/statusTypes"
        render={(props) => {
          dispatch(setPageTitle(<FormattedMessage id="menu.main.scheduledItems.statusTypes" defaultMessage="Status" />, props.location.pathname))
          return <ScheduledStatusTypePage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.ScheduledItems)}
        exact
        path="/ScheduledItems/visibilityTypes"
        render={(props) => {
          dispatch(setPageTitle(<FormattedMessage id="menu.main.scheduledItems.visibilityTypes" defaultMessage="Visibility" />, props.location.pathname))
          return <ScheduledVisibilityTypePage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.BlankForms)}
        exact
        path={['/form/templates', '/form/templates/:id']}
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.forms.templates" defaultMessage="Blank Forms" />,
              props.location.pathname
            )
          )
          return <FormsSettingsPage />
        }}
      />
      <ProtectedRoute
        access={(() => {
          // Allow entry if the user has access to ANY settings-related module.
          // SettingsPage itself filters which specific tabs are visible.
          const settingsModules = [
            Module.CompanySettings,   Module.OrganizationChart,
            Module.CompanyProfile,    Module.UserProfile,
            Module.SecuritySettings,  Module.SystemPreferences,
            Module.Notifications,     Module.ActivityLog,
            Module.RolesPermissions,
          ]
          return settingsModules.map(m => moduleAccess.getAccess(m)).find(a => a.AccessAllowed)
            ?? moduleAccess.getAccess(Module.CompanySettings) // fallback — AccessAllowed will be false
        })()}
        exact
        path="/settings/company"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.company.settings" defaultMessage=" Company Settings " />,
              props.location.pathname
            )
          )
          return <SettingsPage />
        }}
      />
      <Route
        exact
        path="/locationTracking/picklist"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.settings.picklists" defaultMessage="Settings / Picklists" />,
              props.location.pathname
            )
          )
          return <PicklistSettingsPage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/usercertificates"
        render={(props) => {
          dispatch(
            setPageTitle(
                <FormattedMessage id="menu.main.Course" defaultMessage="User Certifications" />,
              props.location.pathname
            )
          )
         return <UserCertificates />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/usercertificates/:userId"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="courses.edit.title" defaultMessage="Edit User Certifications" />,
              props.location.pathname
            )
          )
          return <EditUserCourses />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/policies"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.policies" defaultMessage="User Policies" />,
              props.location.pathname
            )
          )
          return <UserPolicies />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/policies/edit/:userId"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="userPolicies.editTitle" defaultMessage="Edit User Policy Compliance" />,
              props.location.pathname
            )
          )
          return <EditUserPolicyCompliance />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/companypolicies"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.companyPolicies" defaultMessage="User Policies Management" />,
              props.location.pathname
            )
          )
          return <CompanyPolicies />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/courses"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.courses" defaultMessage="Courses" />,
              props.location.pathname
            )
          )
          return <Courses />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatirx/Privatecourses"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.privatecoures" defaultMessage="LMS Courses" />,
              props.location.pathname
            )
          )
          return <PrivateCourses />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.TrainingMatrix)}
        exact
        path="/trainingmatrix/configuration"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="trainingmatrix.config.title" defaultMessage="Training Matrix Configuration" />,
              props.location.pathname
            )
          )
          return <TrainingMatrixConfiguration />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.People)}
        exact
        path="/UsersList"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.settings.users" defaultMessage="Users" />,
              props.location.pathname
            )
          )
          return <UserListPage />
        }}
      />
      <Route
        exact
        path="/communication/emailTemplates"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.settings.EmailTemplate" defaultMessage="Email Templates" />,
              props.location.pathname
            )
          )
          return <EmailTemplatesPage />
        }}
      />
      <Route
        exact
        path="/communication/companyMessages"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.settings.CompanyMessages" defaultMessage="Company Messages" />,
              props.location.pathname
            )
          )
          return <CompanyMessagesPage />
        }}
      />
     
      <Route
        exact
        path="/settings/counters"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.settings.counters" defaultMessage="Counters" />,
              props.location.pathname
            )
          )
          return <Counters />
        }}
      />
      <Route
        exact
        path="/communication/emailLog"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.setting.EmailLog" defaultMessage="Email Log" />,
              props.location.pathname
            )
          )
          return <EmailLogPage />
        }}
      />
      {/* <Route
        exact
              path="/setting/events"
        render={() => {
            return <EventsPage />
        }}
      /> */}
      {/* TODO */}
      <Route
        exact
        path="/setting/globalsettings"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.setting.globalSettings" defaultMessage="Global Settings" />,
              props.location.pathname
            )
          )
          return <GlobalSettingsPage />
        }}
      />
      <Route
        exact
        path="/setting/camerasettings"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.settings.camera" defaultMessage="Camera Settings" />,
              props.location.pathname
            )
          )
          return <CameraSettingPage />
        }}
      />

      <Route
        exact
        path="/communication/smsLog"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.settings.SMSLog" defaultMessage="SMS Log" />,
              props.location.pathname
            )
          )
          return <SMSLogPage />
        }}
      />
      <Route
        exact
        path="/settings/assignmentStatus"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage
                id="menu.main.settings.AssignmentStatus"
                defaultMessage="Assignment Status"
              />,
              props.location.pathname
            )
          )
          return <AssignmentStatusPage />
        }}
      />

      <Route
        exact
        path="/locationTracking/tracker"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.location.tracker" defaultMessage="Tracker" />,
              props.location.pathname
            )
          )
          return <TrackingPage />
        }}
      />
      <Route
        exact
        path="/communication/directMessage"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.setting.directMessage" defaultMessage="Direct Message" />,
              props.location.pathname
            )
          )
          return <DirectMessage />
        }}
      />
      <Route
        exact
        path="/settings/user/:id?"
        render={(props) => {
          return <UserProfilePage {...props} />
        }}
      />
      <Route
        exact
        path="/profile"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.settings.user-profile-me" defaultMessage="My Profile" />,
              props.location.pathname
            )
          )
          return <UserProfilePage user={profile} {...props} />
        }}
      />
      <Route
        path="/securityModules"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.settings.security" defaultMessage="Security" />,
              props.location.pathname
            )
          )
          return <SecurityPage />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Geospatial)}
        exact
        path="/locationTracking/raster"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.location.rasters" defaultMessage="Rasters" />,
              props.location.pathname
            )
          )
          return <RasterPage />
        }}
      />
      <Route
        exact
        path="/locationTracking/shapeFiles"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.location.shapeFiles" defaultMessage="Shape Files" />,
              props.location.pathname
            )
          )
          return <ShapeFilesPage />
        }}
      />
      <Route
        exact
        path="/assets/sitesLocations"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage
                id="menu.main.sites.locations.you"
                defaultMessage="Sites & Locations"
              />,
              props.location.pathname
            )
          )
          return <SitesLocations {...props} />
        }}
      />
      <Route
        exact
        path="/assets/picklists"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.picklists" defaultMessage="Asset Configuration" />,
              props.location.pathname
            )
          )
          return <Picklists {...props} />
        }}
      />
      <Route
        exact
        path="/assets/maintenanceSchedules"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage
                id="menu.main.maintenance.schedules"
                defaultMessage="Maintenance Schedules"
              />,
              props.location.pathname
            )
          )
          return <MaintenanceSchedules {...props} />
        }}
      />
      <Route
        exact
        path="/assets/assetsmanagement"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.assets" defaultMessage="Assets List" />,
              props.location.pathname
            )
          )
          return <AssetsManagement {...props} />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.JourneyManagement)}
        exact
        path="/journeyManagement"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.journey" defaultMessage="Journey Management" />,
              props.location.pathname
            )
          )
          return <JourneyManagementDashboard />
        }}
      />
      <Route
        exact
        path="/locationTracking"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="menu.main.location.tracking.map" defaultMessage="Location Tracking" />,
              props.location.pathname
            )
          )
          return <LocationTrackingDashboard />
        }}
      />
      <ProtectedRoute
        access={moduleAccess.getAccess(Module.Geospatial)}
        exact
        path="/locationTracking/UserGeneratedShapes"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage
                id="menu.main.location.userGeneratedShapes"
                defaultMessage="User Generated Shapes"
              />,
              props.location.pathname
            )
          )
          return <UserGeneratedShapesPage />
        }}
      />
      <Route
        exact
        path="/Forbidden"
        render={(props) => {
          dispatch(
            setPageTitle(
              <FormattedMessage id="title.forbidden" defaultMessage="Forbidden" />,
              props.location.pathname
            )
          )
          return <Forbidden />
        }}
      />
      <Route
        render={(props) => {
          return <NoMatch />
        }}
      />


    </Switch>
  )

  React.useEffect(() => {
    const api = new WorkawareApi()
    let cancelled = false
    // These three are independent of the critical user/role/locale chain below and aren't
    // needed for the very first paint. Staggering them slightly after mount keeps them from
    // competing with the current page's own data fetch for backend connections during the
    // heavier first-load burst (module access, global db id, company message dialog).
    const deferredTimer = setTimeout(() => {
      if (cancelled || !user) return
      api.modulesAccess.get().then((result) => {
        if (cancelled) return
        dispatch(userModuleAccessLoaded(result))
      })
      api.user.getDbId(user.profile.user_id).then((result) => {
        if (cancelled) return
        ;(window as any).globalDbId = result.value;
      })
      api.companyMessage.getCurrentForUser()
        .then((result) => {
          if (cancelled) return
          SetCompanyMessage(result)
        }).catch((error) => {
          if (error?.response?.status === 404) {
            return
          }
          console.error("Error fetching current company message:", error)
        })
    }, 300)

    if (user) {
      ;(async () => {
        const result = await api.user.get(user.profile.user_id)

        // Hydrate Role nav so admin-only gates (profile.Role?.IsAdminLike)
        // render correctly. The OData /odata/Users(id) endpoint doesn't
        // include the Role navigation property by default, so we fetch
        // the company's roles and attach the matching one. Wrapped in
        // try/catch — on failure the SecurityLevel fallback path still
        // works.
        if (result?.RoleId) {
          try {
            const roles = await api.roles.list()
            const matched = (roles || []).find((r: any) => r.Id === result.RoleId)
            if (matched) (result as any).Role = matched
          } catch {
            // Non-fatal — admin gates fall back to SecurityLevel mirror.
          }
        }

        dispatch(userLoaded(result))

        // Apply the user's saved display language to react-intl. The
        // userReducer intentionally strips localeCode from localStorage,
        // so without this dispatch a Chinese-set user would log back in
        // and see English. DefaultLanguageCode is preferred (System
        // Preferences writes to both Language and DefaultLanguageCode).
        const savedLanguage = result?.DefaultLanguageCode || result?.Language
        if (savedLanguage) {
          dispatch(setLocale(savedLanguage))
        }

        // Load user's date/time format preferences
        try {
          const sysprefs = await api.user.getSystemPreferences()
          if (sysprefs?.DateFormat) dispatch(setDateFormat(sysprefs.DateFormat))
          if (sysprefs?.TimeFormat) dispatch(setTimeFormat(sysprefs.TimeFormat))
        } catch {
          // Non-fatal — defaults stay in place
        }
      })()
    }
    if (profile && !user) {
      dispatch(userUnloaded())
      dispatch(userModuleAccessCleared())
    }

    return () => {
      cancelled = true
      clearTimeout(deferredTimer)
    }
    // eslint-disable-next-line
  }, [user])

  return (
    <>
      <Helmet titleTemplate="Workaware: %s" defaultTitle="Workaware.Online">
        <meta name="description" content="Workaware Online Client" />
      </Helmet>
      <Switch>
        {/* Public anonymous form routes — no auth or navigation chrome required.
            IMPORTANT: specific protected paths that share the /forms/* prefix must
            appear BEFORE the wildcard /forms/:id so the Router matches them first. */}
        <Route exact path="/forms/completed" render={() => (
          <Navigation showTopNavigation={false}>
            <Suspense fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>
            }>
              {user ? getLoggedInRoutes() : getAnonymousRoutes()}
            </Suspense>
          </Navigation>
        )} />
        <Route exact path="/forms/:id" render={() => <PublicAnonymousFormPage />} />
        <Route exact path="/forms/entry/:id" render={() => <PublicAnonymousFormPage />} />
        <Route exact path="/forms/:id/:formid" render={() => <PublicAnonymousFormPage />} />
        <Route exact path="/assets/view/:id" render={() => <AssetDetailPage />} />
        <Route exact path="/asset-edit-requests/:token" render={() => <AssetEditRequestResolvePage />} />
        {/* SSO callback and relay — must be OUTSIDE the user?/auth guard so they render
            regardless of whether a previous session is cached in the OIDC store. */}
        <Route exact path="/auth/callback" render={() => (
          <Suspense fallback={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          }>
            <SsoCallback />
          </Suspense>
        )} />
        <Route exact path="/sso-relay" render={() => (
          <Suspense fallback={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          }>
            <SsoRelay />
          </Suspense>
        )} />
        {/* Reset-password renders with NO navigation chrome — no sidebar, topbar, or chat icon */}
        <Route exact path="/reset-password" render={() => (
          <Suspense fallback={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          }>
            <ResetPasswordPage />
          </Suspense>
        )} />
        <Route render={() => (
          <Navigation showTopNavigation={false}>
            <Suspense fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTop: '3px solid #2d7dd2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>
            }>
              <AppReadyNotifier onReady={() => setAppReady(true)} />
              {user ? getLoggedInRoutes() : getAnonymousRoutes()}
            </Suspense>
          </Navigation>
        )} />
      </Switch>
      {companyMessage && dialogReady && canSeeCompanyMessage &&
        <CompanyMessageDialog
          open={open}
          item={companyMessage}
          handleClose={() => Acknowledge()}
        />
      }
      {user && window.location.pathname !== '/reset-password' && !window.location.pathname.startsWith('/forms/') && !window.location.pathname.startsWith('/assets/view/') && (
        <AIChatComponent
          userId={user.profile.user_id}
          companyId={user.profile.company_id} />
      )}
      {user && window.location.pathname !== '/reset-password' && !window.location.pathname.startsWith('/forms/') && !window.location.pathname.startsWith('/assets/view/') && (
        <SessionTimeoutManager />
      )}
    </>
  )
}

export default App
